import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import pricingConfig from "@/shared/pricing.json";

// Single source of truth shared with the backend (`/app/shared/pricing.json`).
// Backend serves the same file at GET /api/billing/plans.
const FALLBACK_PLANS = pricingConfig.plans;

export default function Pricing() {
    const [plans, setPlans] = useState(FALLBACK_PLANS);
    const [yearly, setYearly] = useState(false);

    useEffect(() => {
        api.get("/billing/plans")
            .then(({ data }) => {
                if (Array.isArray(data?.plans) && data.plans.length) setPlans(data.plans);
            })
            .catch(() => { /* keep fallback */ });
    }, []);

    return (
        <div className="min-h-screen bg-ocean-mesh">
            <header className="glass-panel">
                <div className="max-w-7xl mx-auto h-16 px-6 flex items-center justify-between">
                    <Link to="/"><Logo /></Link>
                    <div className="flex gap-3">
                        <Link to="/login"><Button variant="ghost">Sign in</Button></Link>
                        <Link to="/register"><Button className="bg-ocean-700 text-white rounded-xl">Get started</Button></Link>
                    </div>
                </div>
            </header>

            <section className="max-w-7xl mx-auto px-6 py-20 lg:py-28">
                <div className="text-center max-w-3xl mx-auto">
                    <div className="text-xs uppercase tracking-[0.2em] text-secondary-dark font-semibold">Pricing</div>
                    <h1 className="mt-3 font-heading text-5xl sm:text-6xl tracking-tight text-ocean-700">Pay for growth, not for guessing.</h1>
                    <p className="mt-5 text-muted-foreground text-lg">Three honest plans. Cancel anytime. Your data is always yours.</p>
                    <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-sand-300 bg-white px-4 py-2">
                        <span className={`text-sm ${!yearly ? "text-ocean-700 font-medium" : "text-muted-foreground"}`}>Monthly</span>
                        <Switch checked={yearly} onCheckedChange={setYearly} data-testid="billing-cycle-toggle" />
                        <span className={`text-sm ${yearly ? "text-ocean-700 font-medium" : "text-muted-foreground"}`}>Yearly</span>
                        <Badge className="bg-coral/10 text-coral-dark border-0 ml-1">Save 17%</Badge>
                    </div>
                </div>

                <div className="mt-14 grid lg:grid-cols-3 gap-6">
                    {plans.map((p) => {
                        const price = yearly ? p.price_yearly : p.price_monthly;
                        const isPro = p.id === "pro";
                        return (
                            <div
                                key={p.id}
                                data-testid={`plan-card-${p.id}`}
                                className={`rounded-3xl bg-white border p-8 shadow-card flex flex-col ${
                                    isPro ? "border-secondary ring-2 ring-secondary/20 lg:scale-[1.02]" : "border-sand-200"
                                }`}
                            >
                                <div className="flex items-center justify-between">
                                    <h3 className="font-heading text-2xl text-ocean-700">{p.name}</h3>
                                    {isPro && <Badge className="bg-secondary text-white border-0">Most popular</Badge>}
                                </div>
                                <p className="mt-1 text-sm text-muted-foreground">{p.tagline}</p>
                                <div className="mt-7 flex items-end gap-1">
                                    <div className="font-heading text-5xl text-ocean-700">${price}</div>
                                    <div className="text-sm text-muted-foreground mb-2">/{yearly ? "year" : "mo"}</div>
                                </div>
                                <ul className="mt-7 space-y-3 flex-1">
                                    {p.features.map((f) => (
                                        <li key={f} className="flex gap-3 text-sm text-ocean-700">
                                            <Check className="h-4 w-4 text-secondary shrink-0 mt-0.5" />
                                            <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>
                                <Link to="/register" className="mt-8 block">
                                    <Button
                                        className={`w-full h-11 rounded-xl ${
                                            isPro ? "bg-ocean-700 hover:bg-ocean-700/90 text-white"
                                                  : "bg-white border border-sand-300 text-ocean-700 hover:bg-sand-100"
                                        }`}
                                        data-testid={`plan-cta-${p.id}`}
                                    >
                                        {p.id === "free" ? "Start free" : `Choose ${p.name}`}
                                        <ArrowRight className="h-4 w-4 ml-1.5" />
                                    </Button>
                                </Link>
                            </div>
                        );
                    })}
                </div>

                <p className="text-center text-xs text-muted-foreground mt-10">
                    All plans support Cayman Islands no-tax mode. PayPal subscription checkout coming soon — set your plan instantly inside the app.
                </p>
                <div className="mt-10 flex items-center justify-center gap-5 text-xs text-muted-foreground">
                    <Link to="/terms" className="hover:text-ocean-700">Terms of Service</Link>
                    <span>·</span>
                    <Link to="/privacy" className="hover:text-ocean-700">Privacy Policy</Link>
                </div>
            </section>
        </div>
    );
}
